import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { http } from "../../components/helper";

export const getFAQList = createAsyncThunk("vendor/getFAQList", async () => {
  const response = await http.get("admin/crm/faqs");
  return response.data;
});
export const getVendorStats = createAsyncThunk("vendor/getVendorStats", async () => {
  const response = await http.get("admin/crm/getstatistics");
  return response.data;
});
export const getSectorList = createAsyncThunk("vendor/getSectorList", async () => {
  const response = await http.get("admin/crm/product/masterentries/sectors");
  return response.data;
});

export const checkVendorVerification = createAsyncThunk(
  "vendor/checkVerification",
  async ({ token }, { rejectWithValue }) => {   
    try {
      const response = await http.get(`vendor/check-verification/${token}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    } 
  }
);

// Vendor lead / form thunks
export const createVendorLead = createAsyncThunk(
  "vendor/createVendorLead",
  async (body, { rejectWithValue }) => {
    try {
      // flatten data so backend receives fields at root
      const payload = {
        ...(body?.data || {}),
        step: body?.step,
      };
      const response = await http.post("vendor-lead/create", payload);
      return response.data;
    } catch (error) {
      console.error("Create Lead Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveVendorStep = createAsyncThunk(
  "vendor/saveVendorStep",
  async (body, { rejectWithValue }) => {
    try {
      const { leadId } = body || {};
      const payload = {
        ...(body?.data || {}),
        step: body?.step,
      };
      const response = await http.put(`vendor-lead/update/${leadId}`, payload);
      return response.data;
    } catch (error) {
      console.error("Save Step Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const completeVendorLead = createAsyncThunk(
  "vendor/completeVendorLead",
  async (body, { rejectWithValue }) => {
    try {
      const { leadId } = body || {};
      const payload = {
        ...(body?.data || {}),
        step: body?.step,
      };
      const response = await http.put(`vendor-lead/complete/${leadId}`, payload);
      return response.data;
    } catch (error) {
      console.error("Complete Lead Error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
)

export const getVendorLead = createAsyncThunk(
  "vendor/getVendorLead",
  async ({ leadId }) => {
    const response = await http.get(`/vendor-lead/get/${leadId}`);
    return response.data;
  }
);

export const verifyVendorEmail = createAsyncThunk(
  "vendor/verifyEmail",
  async ({ token }, { rejectWithValue }) => {
    try {
      const res = await http.get(`/vendor/verify-email?token=${token}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

const initialState = {
  faqList: [],
  faqStatus: "idle",
  stats: { totalVendors: 0, totalProducts: 0 },
  statsStatus: "idle",
  sectorList: [],
  sectorStatus: "idle",
  error: null,
  // vendor lead / form state
  leadData: null,
  leadId: null,
  createStatus: "idle",
  saveStepStatus: "idle",
  completeStatus: "idle",
  getLeadStatus: "idle",
  saveError: null,
  vendorError: null
};

const vendorSlice = createSlice({
  name: "vendor",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FAQ cases
      .addCase(getFAQList.pending, (state) => {
        state.faqStatus = "loading";
      })
      .addCase(getFAQList.fulfilled, (state, action) => {
        state.faqStatus = "succeeded";
        state.faqList = action.payload?.data || [];
      })
      .addCase(getFAQList.rejected, (state, action) => {
        state.faqStatus = "failed";
        state.error = action.error?.message || "Failed to load FAQs";
      })
      // Stats cases
      .addCase(getVendorStats.pending, (state) => {
        state.statsStatus = "loading";
      })
      .addCase(getVendorStats.fulfilled, (state, action) => {
        state.statsStatus = "succeeded";
        state.stats = action.payload?.data || {
          totalVendors: 0,
          totalProducts: 0,
        };
      })
      .addCase(getVendorStats.rejected, (state, action) => {
        state.statsStatus = "failed";
        state.error = action.error?.message || "Failed to load stats";
      })
      // Sector Case
      .addCase(getSectorList.pending, (state) => {
        state.sectorStatus = "loading";
      })
      .addCase(getSectorList.fulfilled, (state, action) => {
        state.sectorStatus = "succeeded";
        state.sectorList = action.payload?.data || [];
      })
      .addCase(getSectorList.rejected, (state, action) => {
        state.sectorStatus = "failed";
        state.error = action.error?.message;
      });
    // Vendor lead / form reducers
    builder
      .addCase(createVendorLead.pending, (state) => {
        state.createStatus = "loading";
        state.saveError = null;
      })
      .addCase(createVendorLead.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        state.leadData = action.payload;
        // API may return leadId at top level or inside data
        state.leadId = action.payload?._id || action.payload?.data?._id || state._id;
      })
      .addCase(createVendorLead.rejected, (state, action) => {
        state.createStatus = "failed";
        state.saveError = action.error?.message || "Failed to create lead";
      })

      .addCase(saveVendorStep.pending, (state) => {
        state.saveStepStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveVendorStep.fulfilled, (state, action) => {
        state.saveStepStatus = "succeeded";
        state.leadData = action.payload;
        state.leadId = action.payload?._id || action.payload?.data?._id || state._id;
      })
      .addCase(saveVendorStep.rejected, (state, action) => {
        state.saveStepStatus = "failed";
        state.saveError = action.error?.message || "Failed to save step";
      })

      .addCase(completeVendorLead.pending, (state) => {
        state.completeStatus = "loading";
        state.saveError = null;
      })
      .addCase(completeVendorLead.fulfilled, (state, action) => {
        state.completeStatus = "succeeded";
        state.leadData = action.payload;
        state.leadId = null;
        // state.leadId = action.payload?._id || action.payload?.data?._id || state._id;
      })
      .addCase(completeVendorLead.rejected, (state, action) => {
        state.completeStatus = "failed";
        state.saveError = action.error?.message || "Failed to complete lead";
      })
      .addCase(getVendorLead.pending, (state) => {
          state.getLeadStatus  = "loading";
      })
      .addCase(getVendorLead.fulfilled, (state, action) => {
          state.getLeadStatus = "succeeded";
         state.leadData = action.payload;
  state.leadId = action.payload?._id || action.payload?.data?._id || null;
      })
      .addCase(getVendorLead.rejected, (state, action) => {
          state.getLeadStatus = "failed";
          state.vendorError = action.error.message;
      })
      .addCase(verifyVendorEmail.pending, (state) => {
        state.vendorError = null;
      } )
      .addCase(verifyVendorEmail.rejected, (state, action) => {
        state.vendorError = action.payload?.message || "Email verification failed";
      }); 
  },
});

export default vendorSlice.reducer;
